/* eslint-disable react/require-default-props */
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import type { ChangeEvent, FC } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown as ChevronDownIcon } from '../../icons/chevron-down';
import { Search as SearchIcon } from '../../icons/search';
import STRING_KEYS from '../../language/keys';
import { style } from './style';

export interface MultiSelectProps {
  label: string;
  onChange: (value: string[]) => void;
  options: { label: string; value: string; key?: string }[];
  value: string[];
  autoComplete?: boolean;
  autoCompleteValue?: string;
  autoCompleteOnChange?: (value: string) => void;
  isLoading?: boolean,
}

const MultiSelect: FC<MultiSelectProps> = (props) => {
  const {
    label,
    onChange,
    options,
    value = [],
    autoComplete,
    autoCompleteValue,
    autoCompleteOnChange,
    isLoading,
  } = props;
  const { t: translate } = useTranslation();
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const [openMenu, setOpenMenu] = useState<boolean>(false);
  const [autoCompleteLocal, setAutocomplete] = useState<string>(autoCompleteValue || '');

  useEffect(() => {
    autoCompleteOnChange?.(autoCompleteLocal);
  }, [autoCompleteLocal, autoCompleteOnChange]);

  const handleOpenMenu = (): void => {
    setOpenMenu(true);
  };

  const handleCloseMenu = (): void => {
    setOpenMenu(false);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    let newValue = [...value];

    if (event.target.checked) {
      newValue.push(event.target.value);
    } else {
      newValue = newValue.filter((item) => item !== event.target.value);
    }

    onChange?.(newValue);
  };

  const autoCompleteChanged = (event: ChangeEvent<HTMLInputElement>): void => {
    setAutocomplete(event.target.value);
  };

  const renderSearchItem = () => {
    const stopImmediatePropagation = (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
    };

    return (
      <MenuItem
        dense
        onClickCapture={ stopImmediatePropagation }
        style={ style.menu.searchItem.style }
        onKeyDown={ (e) => e.stopPropagation() }

      >
        <TextField
          fullWidth
          placeholder={ translate(STRING_KEYS.MULTI_SELECT.SEARCH_PLACEHOLDER) }
          onChange={ autoCompleteChanged }
          value={ autoCompleteLocal }
          InputProps={ {
            startAdornment: (
              <InputAdornment position={ style.menu.searchItem.textPosition }>
                <SearchIcon fontSize={ style.menu.searchItem.textFontSize } />
              </InputAdornment>
            ),
          } }
        />
      </MenuItem>
    );
  };

  const renderMenuItems = () => {
    if (isLoading) {
      return (
        <Box sx={ style.loader.box.sx }>
          <CircularProgress />
        </Box>
      );
    }

    return options.map((option) => (
      <MenuItem
        key={ option.key || option.label }
      >
        <FormControlLabel
          control={ (
            <Checkbox
              checked={ value.includes(option.value) }
              onChange={ handleChange }
              value={ option.value }
            />
          ) }
          label={ option.label }
          sx={ style.menu.item.label.sx }
        />
      </MenuItem>
    ));
  };

  return (
    <>
      <Button
        color={ style.menu.button.color }
        endIcon={ <ChevronDownIcon fontSize={ style.menu.button.fontSize } /> }
        onClick={ handleOpenMenu }
        ref={ anchorRef }
      >
        {label}
      </Button>
      <Menu
        anchorEl={ anchorRef.current }
        onClose={ handleCloseMenu }
        open={ openMenu }
        PaperProps={ style.menu.paperProps }
      >
        { autoComplete && renderSearchItem() }
        <Typography
          sx={ style.resultCounter.sx }
          variant={ style.resultCounter.variant }
        >
          { options.length ?
            translate(STRING_KEYS.MULTI_SELECT.RESULTS, { count: options.length }) :
            translate(STRING_KEYS.MULTI_SELECT.NO_RESULTS)}
        </Typography>
        { renderMenuItems() }
      </Menu>
    </>
  );
};

export default MultiSelect;
